/**
 * Radar Scoring Tier Map — Phase 0 of the Enterprise Scoring Engine.
 *
 * This is the AUTHORITATIVE classification of every production scoring tool into
 * one PRD §3 scoring tier. It is the single source of truth the later phases
 * read from, and the anchor all four tool-inventory surfaces must reconcile to:
 *
 *   1. src/lib/radar/tool-registry.ts   (registry — the canonical 13, per Lloyd)
 *   2. src/types/radar.ts TOOLS          (what /platform/app actually runs + composites)
 *   3. src/lib/radar/scoring.ts TOOL_GROUPS (the scored-section grouping)
 *   4. src/lib/tool-access.ts TOOL_SLUGS (the free /tools/* gate list — a SEPARATE axis)
 *
 * The `slug` field is the bridge: it is the canonical tool id (identical to
 * tool_runs.tool and the /api/.../<slug> route segment). TOOL_SLUGS stays its own
 * free-gate axis and is NOT mutated; surfaces reconcile against `slug` here.
 *
 * IMPORTANT — two unrelated "tier" axes exist in this codebase:
 *   - PRD scoring tier (this file): tier1 | tier2 | tier3 | plumbing — HOW a signal
 *     is evaluated (deterministic / measured / judged / not-scored).
 *   - Access tier (tool-registry.ts `tier: 1|2|3`, tool_runs.tier): free / token / paid.
 * They are orthogonal. Nothing here changes access gating.
 *
 * PRD §3 routing rules, applied verbatim:
 *   (1) Has a definite right answer?            → Tier 1  (parser, NO LLM on the score path)
 *   (2) Measures what a live engine actually did? → Tier 2  (observed, the anchor)
 *   (3) Genuinely subjective, no parser resolves? → Tier 3  (stabilized LLM judgment)
 *   (4) Supports scoring but isn't itself scored? → Plumbing
 *
 * This file is DATA ONLY. It introduces no scoring logic, no weighting, no
 * stabilization — those are Phases 1-5. A "mixed" tool fuses signals from more
 * than one tier and is flagged for decomposition; `tier` is its PRIMARY tier and
 * `embeddedTiers` lists every tier present.
 */

import { getTools } from './tool-registry'

export type ScoringTier = 'tier1' | 'tier2' | 'tier3' | 'plumbing'

export interface TierClassification {
  /** Registry key (src/lib/radar/tool-registry.ts) — the in-app tool key. */
  readonly key: string
  /**
   * Canonical tool id: identical to tool_runs.tool and the /api/.../<slug> route
   * segment. The reconciliation anchor across all four inventory surfaces.
   */
  readonly slug: string
  /** Display name (mirrors the registry). */
  readonly name: string
  /** Primary PRD §3 scoring tier. */
  readonly tier: ScoringTier
  /**
   * True when the tool fuses signals from more than one tier into one grade and
   * must be decomposed in a later phase. When true, `embeddedTiers` lists all
   * tiers present (including the primary).
   */
  readonly mixed: boolean
  /** Every tier present in the tool. Only set when `mixed` is true. */
  readonly embeddedTiers?: readonly ScoringTier[]
  /** One-line §3 routing rationale + the audit note that drives later phases. */
  readonly rationale: string
}

/**
 * The canonical 13 (keys identical to tool-registry.ts). Ordered registry-first
 * so a diff against getTools() is trivial.
 */
export const TIER_MAP: readonly TierClassification[] = [
  // ── Tier 1 — Deterministic (checked, not judged) ──────────────────────────
  {
    key: 'crawl',
    slug: 'crawl-check',
    name: 'Crawl Check',
    tier: 'tier1',
    mixed: false,
    rationale:
      'Bot-access checks have a definite right answer (can user-agent X fetch?). Score is threshold-deterministic; the GPT-4o-mini call is narrative-only and does not touch the score path.',
  },
  {
    key: 'robots',
    slug: 'robots-analyzer',
    name: 'Robots.txt',
    tier: 'tier1',
    mixed: false,
    rationale:
      'Deterministic parse of robots.txt directives. Pass/fail per bot. LLM call is narrative-only.',
  },
  {
    key: 'llms',
    slug: 'llms-validator',
    name: 'llms.txt',
    tier: 'tier1',
    mixed: false,
    rationale:
      'Structure/section validation, pass/fail with a deterministic breakdown sum. LLM call is narrative-only.',
  },
  {
    key: 'readiness',
    slug: 'ai-readiness',
    name: 'AI Readiness',
    tier: 'tier1',
    mixed: false,
    rationale:
      'Composite of Tier-1 infra signals; score is a deterministic fixed-point allocation (30/25/25/15/5). LLM call is narrative-only. NOTE (§4): those point weights are assumed, not calibrated — a weighting concern, not a tier concern.',
  },
  {
    key: 'schemaAudit',
    slug: 'schema-audit',
    name: 'Schema Audit',
    tier: 'tier1',
    mixed: false,
    rationale:
      'JSON-LD presence/validity is fully deterministic. No LLM import at all (model="none"). The cleanest Tier 1 tool — matches the PRD §10 worked example.',
  },
  {
    key: 'aeo',
    slug: 'aeo-auditor',
    name: 'AEO Audit',
    tier: 'tier1',
    mixed: true,
    embeddedTiers: ['tier1', 'tier3'],
    rationale:
      'MIXED, DECOMPOSED in Phase 2. Six categories: Tier 1 (deterministic, parser-only) = speakableSchema(15) + structuredDataQuality(25) + contentFreshness(10) + entityAuthority(10) = 60. Tier 3 proxy = answerFirstStructure(20) + dataExtractability(20) = 40, the two signals the PRD names explicitly as Tier 3 (§3, §10); today deterministic HTML heuristics, kept as proxies pending Phase 4 stabilization. entityAuthority is parser-only (sameAs / Person / DefinedTerm / OG-meta) so it joins Tier 1. runAeoAuditor now emits tiers.tier1 and tiers.tier3Proxy separately and never fuses them into a single tier grade, so a Tier 3 proxy can never outweigh Tier 1 (see AEO_TIER1_CATEGORIES / AEO_TIER3_PROXY_CATEGORIES in aeo-auditor.ts). The legacy overallScore is retained only for the cross-tool dashboard mean.',
  },

  // ── Tier 2 — Measured (observed, not predicted) ───────────────────────────
  {
    key: 'citation',
    slug: 'citation-check',
    name: 'Citations',
    tier: 'tier2',
    mixed: false,
    rationale:
      'Live multi-engine query — observed citation outcome, the anchor. NOTE (§11): hit-detection is substring matching on model prose, and 2 of 4 providers (gpt-4o-mini, claude-haiku) do not browse, so a "citation" can be a hallucinated brand mention. Validation owed before trusting it as the anchor.',
  },
  {
    key: 'answer',
    slug: 'answer-engine',
    name: 'Citation Test',
    tier: 'tier2',
    mixed: true,
    embeddedTiers: ['tier2', 'tier3'],
    rationale:
      'MIXED. scoreDirectCitation is the Tier 2 observed anchor. scoreContentAlignment / scoreAnswerQualityMatch approximate subjective quality via keyword heuristics — Tier 3 territory once stabilized. Same hallucinated-citation caveat as `citation`.',
  },
  {
    key: 'promptSov',
    slug: 'prompt-sov',
    name: 'Prompt SOV',
    tier: 'tier2',
    mixed: false,
    rationale:
      'Share of voice vs competitors measured on live engines — observed (§10). Same hit-detection caveat as `citation`.',
  },
  {
    key: 'sourceInfluence',
    slug: 'source-influence',
    name: 'Source Influence',
    tier: 'tier2',
    mixed: false,
    rationale:
      'Top domains shaping AI narratives, extracted from live-engine responses — observed. Same hit-detection caveat.',
  },
  {
    key: 'hallucinationCheck',
    slug: 'hallucination-check',
    name: 'Hallucination Check',
    tier: 'tier2',
    mixed: false,
    rationale:
      'Live-engine factual-accuracy flag — observed (PRD §10 maps this to Tier 2 explicitly).',
  },
  {
    key: 'reddit',
    slug: 'reddit-monitor',
    name: 'Reddit',
    tier: 'tier2',
    mixed: true,
    embeddedTiers: ['tier2', 'tier3'],
    rationale:
      "MIXED, DECOMPOSED in Phase 3 (additive, matching AEO). Observed signal (Mention Volume + Subreddit Diversity + Recency) is the Tier 2 measurement. Sentiment (heuristic proxy) + Organic-vs-Seeded (refineWithGPT LLM judgment) are isolated as a Tier 3 sub-signal: temp 0, versioned prompt (SEEDED_PROMPT_VERSION), labeled tier3Pending. It stays INCLUDED in the legacy overallScore (no score drift) and is emitted separately via tiers.tier3Pending pending Phase 4 stabilization. Reddit is still NOT one of the PRD's four answer engines; final composite placement remains the §5 decision.",
  },
  {
    key: 'youtube',
    slug: 'youtube-monitor',
    name: 'YouTube',
    tier: 'tier2',
    mixed: true,
    embeddedTiers: ['tier2', 'tier3'],
    rationale:
      'MIXED, DECOMPOSED in Phase 3 (additive, matching AEO). Observed signal (Mention Volume + Channel Diversity + Reach + Recency) is the Tier 2 measurement; Sentiment (transcript-grounded LLM judgment, analyzeWithTranscripts) is isolated as a Tier 3 sub-signal: temp 0, versioned prompt (PROMPT_VERSION), labeled tier3Pending. It stays INCLUDED in the legacy overallScore (no score drift) and is emitted separately via tiers.tier3Pending pending Phase 4. Logic lives inline in the route (no lib file). Final composite placement remains the §5 decision.',
  },
] as const

// ── Plumbing (PRD §3 rule 4) — supports scoring, must NOT be scored ──────────
/**
 * Brand detection is the one acceptable GPT-4o-mini job (PRD §3, §10). These are
 * the 14th+ tools, deliberately NOT in the canonical 13:
 *   - brandDisambiguation (slug brand-disambiguation): entity-confusion flags;
 *     currently emits an overallScore/grade and writes a scored tool_runs row.
 *     Per the PRD, plumbing is "not scored." De-scoring it is a reconciliation
 *     task (still wired into src/types/radar.ts TOOLS + the client composite),
 *     folded into Phase 1's brandDisambiguation→youtube swap.
 *   - detect-brand: pure brand resolution used by other tools; never scored.
 */
export const PLUMBING_TOOLS = ['brandDisambiguation', 'detect-brand'] as const

// ── Accessors (pure, no scoring logic) ───────────────────────────────────────

export function getClassification(key: string): TierClassification | undefined {
  return TIER_MAP.find(t => t.key === key)
}

/** Look up a classification by canonical slug (tool_runs.tool / route segment). */
export function getClassificationBySlug(
  slug: string
): TierClassification | undefined {
  return TIER_MAP.find(t => t.slug === slug)
}

/**
 * Keys whose PRIMARY tier matches `tier`. Pass includeEmbedded to also return
 * mixed tools that merely contain `tier` as a non-primary signal (e.g. the
 * Tier-3 determinism work needs every tool with a Tier 3 component).
 */
export function keysForTier(
  tier: ScoringTier,
  opts: { includeEmbedded?: boolean } = {}
): string[] {
  return TIER_MAP.filter(t =>
    opts.includeEmbedded
      ? t.tier === tier || (t.embeddedTiers?.includes(tier) ?? false)
      : t.tier === tier
  ).map(t => t.key)
}

/** Tools that fuse more than one tier and must be decomposed in a later phase. */
export function mixedTools(): readonly TierClassification[] {
  return TIER_MAP.filter(t => t.mixed)
}

/**
 * Guard for a future test: the tier map MUST classify exactly the registry's
 * tools (no more, no fewer). Returns the symmetric difference; empty array means
 * the four surfaces have a coherent anchor. Throws nothing — callers assert.
 */
export function tierMapRegistryDiff(): {
  missingFromTierMap: string[]
  notInRegistry: string[]
} {
  const registryKeys = new Set(getTools().map(t => t.key))
  const tierMapKeys = new Set(TIER_MAP.map(t => t.key))
  return {
    missingFromTierMap: [...registryKeys].filter(k => !tierMapKeys.has(k)),
    notInRegistry: [...tierMapKeys].filter(k => !registryKeys.has(k)),
  }
}
